/**
 * @fileoverview ページ下部に表示されるバナーの基本レイアウトコンポーネント
 */
<script lang="ts">
import { defineComponent, PropType, ref, computed, watch } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import ButtonClose from '@/components/button/ButtonClose.vue'
import { pathWithQueryParams } from '@/utilities/path'

const DEFAULT_IMAGE_WIDTH = {
  pc: 142,
  sp: 142,
} as const

export default defineComponent({
  name: 'FooterBannerCore',

  components: {
    ButtonSubmit,
    ButtonClose,
  },

  props: {
    caption: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    /**
     * バナー右部に表示される CTA ボタンのラベル
     */
    ctaLabel: {
      type: String,
      default: '',
    },

    /**
     * バナー左部に表示される画像
     */
    image: {
      type: String,
      default: '',
    },

    /**
     * 画像領域の幅（px 単位）
     */
    imageWidth: {
      type: Object as PropType<{
        pc: number,
        sp: number,
      }>,
      default: () => ({
        pc: DEFAULT_IMAGE_WIDTH.pc,
        sp: DEFAULT_IMAGE_WIDTH.sp,
      }),
      validator: (value: {
        pc: number,
        sp: number,
      }) => value.pc > 0 && value.sp > 0,
    },

    /**
     * 遷移先パス
     */
    to: {
      type: String,
      default: '',
    },

    /**
     * CTA ボタンに付与するクエリパラメータ
     */
    parameters: {
      type: Array as PropType<QueryParameter[]>,
      default: () => [],
    },

    isShow: {
      type: Boolean,
      default: undefined,
    },
  },

  emits: ['close'],

  setup(props, { emit }) {
    const isShowLocal = ref<boolean>(false)

    /**
     * バナーのラッパー DOM のタグ名
     * @description 遷移先パスが指定されており、説明文が存在しない場合は a タグ（バナー全体がアンカーとなる）、それ以外は div タグを返す
     */
    const wrapperTagName = computed<string>(() => {
      return !!props.to && !props.description
        ? 'a'
        : 'div'
    })

    const ctaPath = computed<string>(() => pathWithQueryParams(props.to, props.parameters))

    /**
     * 不正な値等を弾いたレンダリング画像 width の数値（px 単位）
     */
    const computedImageWidth = computed<{ pc: number, sp: number }>(() => {
      const { pc, sp } = props.imageWidth
      return {
        pc: pc && pc > 0 ? pc : DEFAULT_IMAGE_WIDTH.pc,
        sp: sp && sp > 0 ? sp : DEFAULT_IMAGE_WIDTH.sp,
      }
    })

    watch(() => props.isShow, (newVal) => {
      isShowLocal.value = newVal ?? true
    }, { immediate: true })

    const onClose = () => {
      isShowLocal.value = false
      emit('close', isShowLocal.value)
    }

    return {
      // refs
      isShowLocal,

      // computed
      wrapperTagName,
      ctaPath,
      computedImageWidth,

      // methods
      onClose,
    }
  },
})
</script>

<template>
  <transition name="is-popup">
    <section
      v-if="isShowLocal"
      class="f-section__footerBanner"
      :data-wide="description ? true : false">
      <div class="f-section__footerBanner--body">
        <component
          :is="wrapperTagName"
          class="l-unit__footerAdBanner"
          :style="{
            '--image-width-pc': `${computedImageWidth.pc}px`,
            '--image-width-sp': `${computedImageWidth.sp}px`,
          }"
          :data-direction="description ? 'horizontal' : 'vertical'"
          :href="wrapperTagName === 'a' ? ctaPath : undefined"
          :target="wrapperTagName === 'a' ? '_blank' : undefined">
          <div
            v-if="$slots.title"
            class="l-unit__footerAdBanner--header">
            <slot name="title" />
          <!-- /.l-unit__footerAdBanner header --></div>

          <div class="l-unit__footerAdBanner--image">
            <img
              class="img"
              :src="image"
              loading="lazy"
              data-is-liquid="true"
              alt="">
          <!-- /.l-unit__footerAdBanner image --></div>

          <div
            v-if="description"
            class="l-unit__footerAdBanner--description">
            <p class="p u-text__small">
              {{ description }}
            </p>
          <!-- /.l-unit__footerAdBanner description --></div>

          <div
            v-if="!!description && !!to && !!ctaLabel"
            class="l-unit__footerAdBanner--action is-cta">
            <ButtonSubmit
              :href="ctaPath"
              target="_blank"
              :width="240">
              {{ ctaLabel }}
            </ButtonSubmit>
          <!-- /.l-unit__footerAdBanner action --></div>

          <div class="l-unit__footerAdBanner--action is-close">
            <ButtonClose
              :color="description ? 'gray' : 'white'"
              :background="description ? 'transparent' : 'gray'"
              :has-tooltip="false"
              prevent
              stop
              @click:close="onClose" />
          <!-- /.l-unit__footerAdBanner action --></div>

          <div
            v-if="caption"
            class="l-unit__footerAdBanner--footer is-caption">
            <div class="o-label__caption">
              <p class="o-label__caption--item">
                {{ caption }}
              </p>
            <!-- /.o-label__caption --></div>
          <!-- /.l-unit__footerAdBanner footer --></div>
        <!-- /.l-unit__footerAdBanner --></component>
      <!-- /.f-section__footerBanner body --></div>
    <!-- /.f-section__footerBanner --></section>
  </transition>
</template>

<style lang="sass" scoped>
// foundation
.f-section__footerBanner
  $root: &
  width: 100%
  position: fixed
  bottom: 0
  padding-bottom: 0.5em
  z-index: 1

  & &--body
    position: relative
    margin: 0 auto

  &[data-wide="true"]
    max-width: #{$global-width-pc}px
    left: 50%
    transform: translateX(-50%)
    padding:
      left: #{$global-prop-pc}px
      right: #{$global-prop-pc}px

  &[data-wide="false"]
    $wrapper-width: 330
    max-width: #{$wrapper-width + ($global-prop-pc * 2)}px
    right: 0
    padding:
      left: #{$global-prop-pc}px
      right: #{$global-prop-pc}px

  +breakpoint
    padding-bottom: #{$global-prop-sp}px

    &[data-wide="true"]
      padding:
        left: #{$global-prop-sp}px
        right: #{$global-prop-sp}px

    &[data-wide="false"]
      $wrapper-width: 330
      max-width: #{$wrapper-width + ($global-prop-sp * 2)}px
      padding:
        left: #{$global-prop-sp}px
        right: #{$global-prop-sp}px

// layout
.l-unit__footerAdBanner
  $root: &
  width: 100%
  display: grid
  align-items: start
  position: relative
  color: inherit
  text-decoration: none
  background: $bg-white
  border-radius: 8px

  & &--header
    line-height: 1.4

  & &--action
    &.is-close
      position: absolute

  /**
   * 【レイアウト】：横型
   * 【サイズ】：PC 以上
   * 【グリッドテンプレートエリア】：
   * img  img-col-gap  caption     caption-col-gap  header      button-col-gap button
   * img  img-col-gap  description description      description button-col-gap button
   */
  &[data-direction="horizontal"]
    $image-col-gap: 1.5em
    $caption-col-gap: 0.75em
    $button-col-gap: 0.5em
    $button-width: 184px
    $cta-button-right-gutter: 40px
    grid-template-columns: var(--image-width-pc) $image-col-gap auto $caption-col-gap 1fr $button-col-gap $button-width
    row-gap: 0.25em
    margin: 0 auto
    padding: 0.75em calc(1em + $cta-button-right-gutter) 0.75em 1em
    border: 1px solid $border-orange

    #{$root}--header
      grid-column: 5 / 6
      grid-row: 1 / 2

    #{$root}--image
      grid-column: 1 / 2
      grid-row: 1 / 3

    #{$root}--description
      grid-column: 3 / 6
      grid-row: 2 / 3

    #{$root}--action
      &.is-cta
        grid-column: 7 / 8
        grid-row: 1 / 3
        align-self: center

      &.is-close
        $close-button-size: 40px
        width: $close-button-size
        height: $close-button-size
        top: 2px
        right: 2px

    #{$root}--footer.is-caption
      grid-column: 3 / 4
      grid-row: 1 / 2

  /**
   * 【レイアウト】：縦型
   * 【サイズ】：PC 以上
   * 【グリッドテンプレートエリア】：
   * img      img
   * caption  header
   */
  &[data-direction="vertical"]
    grid-template-columns: auto 1fr
    align-items: start
    padding: 0.5em
    box-shadow: 0 4px 4px rgba($bg-black, 0.1)

    #{$root}--header
      grid-column: 2 / 3
      grid-row: 2 / 3

    #{$root}--image
      grid-column: 1 / 3
      grid-row: 1 / 2

    #{$root}--action
      &.is-close
        $close-button-size: 20px
        width: $close-button-size
        height: $close-button-size
        top: -0.5em
        right: -0.5em

    #{$root}--footer
      &.is-caption
        grid-column: 1 / 2
        grid-row: 2 / 3

    // キャプションか見出しのいずれかが存在する場合は縦方向の間隔を設定
    &:has(#{$root}--footer.is-caption),
    &:has(#{$root}--header)
      row-gap: 0.5em

    // キャプションと見出し両方が存在する場合は横方向の間隔を設定
    &:has(#{$root}--footer.is-caption):has(#{$root}--header)
      column-gap: 0.5em

  +breakpoint($max-media: pc)
    /**
     * 【レイアウト】：横型
     * 【サイズ】：Phablet 〜 Tablet
     *
     * 【グリッドテンプレートエリア】：
     * img  img-col-gap  caption          caption-col-gap button-col-gap button
     * img  img-col-gap  caption-row-gap  caption-col-gap button-col-gap button
     * img  img-col-gap  header           header          button-col-gap button
     * img  img-col-gap  description      description     button-col-gap button
     */
    &[data-direction="horizontal"]
      $image-col-gap: changeSizePx2Em(14)
      $caption-row-gap: 0.25em
      $button-col-gap: 0.5em
      $button-width: 184px
      grid-template-columns: var(--image-width-pc) $image-col-gap auto 1fr $button-col-gap $button-width
      grid-template-rows: auto $caption-row-gap auto auto
      row-gap: 0
      padding: 0.75em 1em

      #{$root}--header
        grid-column: 3 / 5
        grid-row: 3 / 4

      #{$root}--image
        grid-column: 1 / 2
        grid-row: 1 / 5

      #{$root}--description
        grid-column: 3 / 5
        grid-row: 4 / 5

      #{$root}--action
        &.is-cta
          grid-column: 6 / 7
          grid-row: 1 / 5

  +breakpoint
    /**
     * 【レイアウト】：横型
     * 【サイズ】：Phablet 以下
     *
     * 【グリッドテンプレートエリア】：
     * img          img-col-gap   caption         caption-col-gap
     * img          img-col-gap   caption-row-gap caption-col-gap
     * img          img-col-gap   header          header
     * desc-row-gap desc-row-gap  desc-row-gap    desc-row-gap
     * description  description   description     description
     * btn-row-gap  btn-row-gap   btn-row-gap     btn-row-gap
     * button       button        button          button
     */
    &[data-direction="horizontal"]
      $image-col-gap: changeSizePx2Em(14)
      $caption-row-gap: 0.25em
      $description-row-gap: 0.75em
      $button-col-gap: 0.5em
      $button-row-gap: 0.75em
      grid-template-columns: var(--image-width-sp) $image-col-gap auto 1fr
      grid-template-rows: auto $caption-row-gap 1fr $description-row-gap auto $button-row-gap auto

      #{$root}--header
        grid-column: 3 / 5
        grid-row: 3 / 4

      #{$root}--image
        grid-column: 1 / 2
        grid-row: 1 / 4

      #{$root}--description
        grid-column: 1 / 5
        grid-row: 5 / 6

      #{$root}--action
        &.is-cta
          grid-column: 1 / 5
          grid-row: 7 / 8

      #{$root}--footer
        &.is-caption
          grid-column: 3 / 4
          grid-row: 1 / 2

    /**
     * 【レイアウト】：縦型
     * 【サイズ】：PC 以上
     * 【グリッドテンプレートエリア】：
     * img      img
     * caption  header
     */
    &[data-direction="vertical"]
      #{$root}--header
        grid-column: 1 / 3
        grid-row: 3 / 4

      #{$root}--image
        grid-column: 1 / 3
        grid-row: 1 / 2

      #{$root}--footer
        &.is-caption
          grid-column: 1 / 2
          grid-row: 2 / 3

// object
.o-label__caption
  $root: &

  & &--item
    line-height: 1
    color: $font-dark-orange
    font:
      size: $font-small
      weight: bold
    padding: changeSizePx2Em(4, $font-small-px)
    border: 1px solid $border-orange

  +breakpoint
    #{$root}--item
      font-size: $font-x-small
</style>
